import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Checkbox,
  useTheme
} from '@mui/material';
import { ServiceOpportunity, ServiceDefinition } from '../../models';

interface ServiceOpportunityListProps {
  opportunities: ServiceOpportunity[];
  onToggleOpportunity: (opportunity: ServiceOpportunity) => void;
  selectedOpportunityIds: Set<string>;
  services?: ServiceDefinition[];
}

/**
 * Component for displaying a list of service opportunities
 */
const ServiceOpportunityList: React.FC<ServiceOpportunityListProps> = ({
  opportunities,
  onToggleOpportunity,
  selectedOpportunityIds,
  services = []
}) => {
  const theme = useTheme();

  // Format time for display
  const formatTime = (isoString: string): string => {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate opportunity duration in hours and minutes
  const getDuration = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${durationHours}h ${durationMinutes}m`;
  };

  // Is this opportunity selected?
  const isSelected = (opportunity: ServiceOpportunity): boolean => {
    return selectedOpportunityIds.has(opportunity.id);
  };

  // Check if an unselected opportunity conflicts with any selected opportunity
  const conflictsWithSelected = (opportunity: ServiceOpportunity, index: number): boolean => {
    if (isSelected(opportunity)) {
      return false;
    }
    
    if (!opportunity.conflictIndexes || opportunity.conflictIndexes.length === 0) {
      return false;
    }
    
    return opportunity.conflictIndexes.some(conflictIndex => {
      // Ensure the conflict index is valid
      if (conflictIndex < 0 || conflictIndex >= opportunities.length) {
        return false;
      }
      
      // Get the ID of the conflicting opportunity and check if it's selected
      const conflictId = opportunities[conflictIndex].id;
      return selectedOpportunityIds.has(conflictId);
    });
  };

  // Common table cell styling
  const cellStyle = { 
    padding: '8px 12px', 
    height: '48px', 
    boxSizing: 'border-box' as const
  };
  
  return (
    <Box>
      <Box sx={{ width: '95%', mx: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Service Opportunities {opportunities.length > 0 ? `(${opportunities.length})` : ''}
        </Typography>
        
        {opportunities.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No service opportunities available for this date.
          </Typography>
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              mb: 4,
              overflow: 'hidden' // Prevent horizontal scrolling
            }}
          >
            <Table 
              size="small" 
              sx={{ 
                tableLayout: 'fixed', 
                width: '100%',
                borderCollapse: 'separate', // Prevent border collapsing
                borderSpacing: 0 // Remove default spacing
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellStyle, width: '8%' }} align="center">Select</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '22%' }}>Service</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '14%' }}>Start Time</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '14%' }}>End Time</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '14%' }}>Duration</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '14%' }}>Launch</TableCell>
                  <TableCell sx={{ ...cellStyle, width: '14%' }}>Retrieval</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {opportunities.map((opportunity, index) => {
                  const isRowSelected = isSelected(opportunity);
                  const hasConflict = conflictsWithSelected(opportunity, index);
                  
                  return (
                    <TableRow 
                      key={opportunity.id}
                      selected={isRowSelected}
                      sx={{
                        // Base styling for all rows
                        boxSizing: 'border-box',
                        borderLeft: '4px solid transparent',
                        
                        // Selected styling
                        ...(isRowSelected && {
                          backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
                          borderLeft: `4px solid ${theme.palette.primary.main}`,
                        }),
                        
                        // Conflict styling
                        ...(hasConflict && {
                          opacity: 0.4,
                          backgroundColor: 'rgba(211, 47, 47, 0.05) !important',
                          borderLeft: `4px solid ${theme.palette.error.main}`,
                        }),
                        
                        // Hover styling
                        '&:hover': {
                          backgroundColor: isRowSelected 
                            ? 'rgba(25, 118, 210, 0.18) !important'
                            : theme.palette.action.hover
                        },
                      }}
                    >
                      <TableCell sx={cellStyle} align="center">
                        <Checkbox
                          checked={isRowSelected}
                          onChange={() => onToggleOpportunity(opportunity)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {/* Service color indicator */}
                          {(() => {
                            const serviceDef = services.find(s => s.id === opportunity.serviceId);
                            if (!serviceDef) {
                              console.error(`Service definition not found for ID: ${opportunity.serviceId}`);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.grey[500], mr: 1 }} />
                                  <Typography variant="body2" noWrap color="error">
                                    Unknown Service
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: serviceDef.color, mr: 1 }} />
                                <Typography variant="body2" noWrap>
                                  {serviceDef.name} {/* Look up name here */}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Typography variant="body2">
                          {formatTime(opportunity.startTime)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Typography variant="body2">
                          {formatTime(opportunity.endTime)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Typography variant="body2">
                          {getDuration(opportunity.startTime, opportunity.endTime)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Tooltip title={`Tide height: ${opportunity.tideHeightAtLaunch.toFixed(2)}m`}>
                          <Chip
                            label={opportunity.tideHeightAtLaunch.toFixed(1)}
                            size="small"
                            color={opportunity.launchViable ? 'success' : 'error'}
                            variant={opportunity.launchViable ? 'filled' : 'outlined'}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Tooltip title={`Tide height: ${opportunity.tideHeightAtRetrieval.toFixed(2)}m`}>
                          <Chip
                            label={opportunity.tideHeightAtRetrieval.toFixed(1)}
                            size="small"
                            color={opportunity.retrievalViable ? 'success' : 'error'}
                            variant={opportunity.retrievalViable ? 'filled' : 'outlined'}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default ServiceOpportunityList;
